import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Footer } from '@domgen/base-design-system-2.0/atoms/Footer';
import { HeaderCobranded } from '@domgen/base-design-system-2.0/atoms/HeaderCobranded';
import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import pdfCarePlus from './assets/documents/HIS_USWTCCP_0626_31_100.pdf';
import pdfReplacementCare from './assets/documents/HIS_USWTCRC_0626_03_100.pdf';
import { useAppContext } from './context/AppContext';
import { AppRoute } from './enums/appRoutes';
import { store } from './store/store';

/**
 * Routes that use the SEO-optimised page title rather than the default "Registration - {route}" title.
 * When the brand sets VITE_CANONICAL_URL (production only), these routes also render a canonical link to it.
 */
const SEO_ROUTES = [AppRoute.LANDING, AppRoute.APPLIANCE_DETAILS];

export const Layout = () => {
  const location = useLocation();
  const { clientLogo, getRouteFromPath } = useAppContext();
  const clientName = import.meta.env.VITE_CLIENT_FRIENDLY_NAME.trim();
  const navigate = useNavigate();
  const links = [
    {
      text: `${clientName} Care Plus`,
      href: pdfCarePlus,
    },
    {
      text: `${clientName} Replacement Care`,
      href: pdfReplacementCare,
    },
    {
      text: `${clientName} Product Support`,
      href: `https://www.hisense-usa.com/support`,
    },
    { text: `Terms of Use`, href: 'https://www.domesticandgeneral.com/us/terms-and-conditions' },
    {
      text: `Domestic & General privacy notice`,
      href: 'https://www.domesticandgeneral.com/us/security-privacy',
    },
    {
      text: `${clientName} privacy notice`,
      href: 'https://www.hisense-usa.com/privacy-policy',
    },
    { text: `Cookies notice`, href: 'https://www.domesticandgeneral.com/us/cookies' },
  ];

  useEffect(() => {
    smoothScrollIntoView(0);
  }, [location.pathname]);

  useEffect(() => {
    const sub = store.on('navigate', ({ path, replace }) => {
      navigate(path, { replace });
    });
    return sub.unsubscribe;
  }, [navigate]);

  const isSeoRoute = SEO_ROUTES.includes(location.pathname as AppRoute);
  const canonicalUrl = import.meta.env.VITE_CANONICAL_URL?.trim();

  const getPageTitle = () => {
    if (isSeoRoute) {
      return `Register Your ${clientName} Product | Warranty Signup`;
    }
    const route = getRouteFromPath(location.pathname);
    if (!route) return `${clientName} Registration`;
    return `${clientName} Registration - ${route.title}`;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderCobranded
        clientIcon={
          <NavLink aria-label="Hisense Registration homepage" to="/">
            {clientLogo}
          </NavLink>
        }
        clientName={clientName}
      />
      <main className="grow flex flex-col">
        <title>{getPageTitle()}</title>
        {isSeoRoute && canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        <Outlet />
      </main>
      <Footer links={links} />
    </div>
  );
};
